import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import { Box, GlobalStyles, Stack, Typography, useTheme } from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortDirection,
    GridValidRowModel,
} from "@mui/x-data-grid"
import {
    CSSProperties,
    ReactElement,
    ReactNode,
    useCallback,
    useMemo,
} from "react"

import {
    CustomToolbar,
    CustomToolbarProps,
    ToolbarButton,
    ALL_TOOLBAR_BUTTONS,
} from "./customToolbar"
import { useDataGridColumnPersistence } from "../../../hooks/useDataGridColumnPersistence"
import AppTooltip from "../../helper/appTooltip"

// Define custom action interface
export interface CustomAction<T> {
    key: string
    label: string
    icon: ReactElement
    onClick: (row: T) => void
    color?: CSSProperties["color"]
    tooltip?: string
}

interface AdminDataGridProps<T> {
    /** The data rows to display in the grid */
    rows: T[]
    /** Column configuration for the data grid */
    columns: GridColDef[]
    getRowId: (row: T) => string
    itemTypeText: string
    isLoading: boolean
    /** Whether to show action buttons (edit, copy, delete) */
    showActions?: boolean
    /** Which toolbar buttons to display (default: all) */
    toolbarButtons?: ToolbarButton[]
    /** Whether to show the "All [ItemType]s" title above the grid */
    showTitle?: boolean
    /** Whether to disable row selection on click */
    disableRowSelectionOnClick?: boolean
    onCreateClick?: () => void
    onRowClick?: (row: T) => void
    onEdit?: (row: T) => void
    onCopy?: (row: T) => void
    onDelete?: (row: T) => void
    /** Custom actions to be added to the actions column */
    customActions?: CustomAction<T>[]
    /** Custom buttons to add to the toolbar (e.g., filters, secondary actions) */
    customToolbarButtons?: ReactNode
    columnVisibilityModel?: Record<string, boolean>
    /** Row density: compact, standard, or comfortable */
    density?: "compact" | "standard" | "comfortable"
    sx?: Record<string, never>
    /** Callback fired when a row is updated via cell editing */
    processRowUpdate?: (newRow: T, oldRow: T) => T | Promise<T>
    /** Initial number of rows per page (default: 10) */
    initialPageSize?: number
    /** Callback to apply custom CSS class names to rows */
    getRowClassName?: (params: GridRowParams) => string
    /** Hide the toolbar (filters, columns, export, reorder) */
    hideToolbar?: boolean
    /** Field to sort by initially */
    initialSortField?: string
    /** Sort direction for the initial sort (default: "asc") */
    initialSortDirection?: GridSortDirection
    paginationMode?: "client" | "server"
    rowCount?: number
    paginationModel?: GridPaginationModel
    onPaginationModelChange?: (model: GridPaginationModel) => void
    pageSizeOptions?: number[]
}

/**
 * A reusable component for displaying a data grid with admin actions.
 * This component is designed to unify data grid displays with admin actions
 * such as create, edit, copy, and delete in organizational pages.
 *
 * Features:
 * - Drag and drop column reordering (using custom reorder interface above grid)
 * - Column visibility controls via toolbar
 * - Persistent column state in localStorage
 * - Action buttons for CRUD operations
 * - Row selection and click handling
 * - Responsive design with toolbar
 *
 * @returns {ReactElement} - The rendered AdminDataGrid component.
 */
const AdminDataGrid = <T extends GridValidRowModel>({
    rows,
    columns: baseColumns,
    getRowId,
    itemTypeText,
    isLoading,
    showActions = true,
    toolbarButtons = ALL_TOOLBAR_BUTTONS,
    showTitle = true,
    disableRowSelectionOnClick = false,
    onCreateClick,
    onRowClick,
    onEdit,
    onCopy,
    onDelete,
    customActions = [],
    customToolbarButtons,
    columnVisibilityModel: initialColumnVisibilityModel = {},
    density = "compact",
    sx = {},
    processRowUpdate,
    initialPageSize = 10,
    getRowClassName,
    hideToolbar = false,
    initialSortField,
    initialSortDirection = "asc",
    paginationMode = "client",
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions = [5, 10, 25, 50],
}: AdminDataGridProps<T>): ReactElement => {
    const theme = useTheme()
    // Add actions column if needed and disable column menu for all columns
    const columns: GridColDef[] = useMemo(() => {
        // Add disableColumnMenu to all base columns
        const columnsWithDisabledMenu = baseColumns.map((col) => ({
            ...col,
            disableColumnMenu: true,
        }))

        return showActions
            ? [
                  ...columnsWithDisabledMenu,
                  {
                      field: "actions",
                      type: "actions",
                      headerName: "Actions",
                      flex: 1.2,
                      disableColumnMenu: true,
                      getActions: (params: GridRowParams) => {
                          const actions: ReactElement[] = []

                          if (onEdit) {
                              actions.push(
                                  <GridActionsCellItem
                                      key="edit"
                                      icon={
                                          <AppTooltip
                                              title={`Edit ${itemTypeText}`}
                                          >
                                              <EditIcon />
                                          </AppTooltip>
                                      }
                                      label="Edit"
                                      onClick={() => onEdit(params.row as T)}
                                      color="primary"
                                  />
                              )
                          }

                          if (onCopy) {
                              actions.push(
                                  <GridActionsCellItem
                                      key="copy"
                                      style={{
                                          color: theme.palette.secondary.main,
                                      }}
                                      icon={
                                          <AppTooltip
                                              title={`Copy ${itemTypeText}`}
                                          >
                                              <ContentCopyIcon />
                                          </AppTooltip>
                                      }
                                      label="Copy"
                                      onClick={() => onCopy(params.row as T)}
                                  />
                              )
                          }

                          if (onDelete) {
                              actions.push(
                                  <GridActionsCellItem
                                      key="delete"
                                      icon={
                                          <AppTooltip
                                              title={`Delete ${itemTypeText}`}
                                          >
                                              <DeleteIcon />
                                          </AppTooltip>
                                      }
                                      label="Delete"
                                      onClick={() => onDelete(params.row as T)}
                                      style={{
                                          color: theme.palette.error.dark,
                                      }}
                                  />
                              )
                          }

                          // Add custom actions
                          customActions.forEach((action) => {
                              actions.push(
                                  <GridActionsCellItem
                                      key={action.key}
                                      icon={
                                          action.tooltip ? (
                                              <AppTooltip
                                                  title={action.tooltip}
                                              >
                                                  {action.icon}
                                              </AppTooltip>
                                          ) : (
                                              action.icon
                                          )
                                      }
                                      label={action.label}
                                      onClick={() =>
                                          action.onClick(params.row as T)
                                      }
                                      style={{
                                          color:
                                              action.color ||
                                              theme.palette.primary.main,
                                      }}
                                  />
                              )
                          })

                          return actions
                      },
                  },
              ]
            : columnsWithDisabledMenu
    }, [
        baseColumns,
        showActions,
        onEdit,
        onCopy,
        onDelete,
        customActions,
        itemTypeText,
        theme.palette.error.dark,
        theme.palette.primary.main,
        theme.palette.secondary.main,
    ])

    // Get available column fields
    const availableColumns = columns.map((col) => col.field)

    // Use hook for column persistence
    const {
        columnVisibilityModel,
        columnOrderState,
        handleColumnVisibilityModelChange,
        setColumnOrder,
    } = useDataGridColumnPersistence({
        storageKey: `${itemTypeText.toLowerCase()}-dataGrid-columns`,
        availableColumns,
        defaultVisibility: initialColumnVisibilityModel,
    })

    // Handle column reorder from the interface - use the hook's proper API
    const handleColumnReorderFromInterface = useCallback(
        (newOrder: string[]) => {
            setColumnOrder(newOrder)
        },
        [setColumnOrder]
    )

    // Order columns based on saved state
    const orderedColumns = useMemo(() => {
        if (columnOrderState.length === 0) {
            return columns
        }

        // Create a map for quick lookup
        const columnMap = new Map(columns.map((col) => [col.field, col]))

        // Order columns according to saved state, then add any new columns
        const orderedCols: GridColDef[] = []

        // Add columns in saved order
        columnOrderState.forEach((fieldName) => {
            const column = columnMap.get(fieldName)
            if (column) {
                orderedCols.push(column)
                columnMap.delete(fieldName)
            }
        })

        // Add any remaining columns that weren't in the saved order
        Array.from(columnMap.values()).forEach((column) => {
            orderedCols.push(column)
        })

        return orderedCols
    }, [columns, columnOrderState])

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            onRowClick?.(params.row as T)
        },
        [onRowClick]
    )

    const toolbarSlotProps = useMemo<CustomToolbarProps>(
        () => ({
            onCreateClick,
            columns: orderedColumns,
            onColumnOrderChange: handleColumnReorderFromInterface,
            toolbarButtons,
            customToolbarButtons,
            exportFileName: `${itemTypeText.replace(/\s/g, "").toLowerCase()}-${
                new Date().toISOString().split("T")[0]
            }`,
        }),
        [
            onCreateClick,
            orderedColumns,
            handleColumnReorderFromInterface,
            toolbarButtons,
            customToolbarButtons,
            itemTypeText,
        ]
    )

    return (
        <Box>
            <GlobalStyles
                styles={() => ({
                    ".MuiDataGrid-panel": {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                        border: `1px solid ${theme.palette.divider} !important`,
                        borderRadius: `${theme.shape.borderRadius}px !important`,
                        boxShadow: `${theme.shadows[8]} !important`,
                        zIndex: `${theme.zIndex.modal} !important`,
                        "& .MuiPaper-root": {
                            backgroundColor: `${theme.palette.background.paper} !important`,
                        },
                    },
                    ".MuiDataGrid-panelHeader": {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                        borderBottom: `1px solid ${theme.palette.divider} !important`,
                        padding: "12px 16px !important",
                        fontWeight: "bold !important",
                    },
                    ".MuiDataGrid-panelContent": {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                        padding: "8px !important",
                        maxHeight: "400px !important",
                        overflow: "auto !important",
                    },
                    ".MuiDataGrid-columnsPanel": {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                        "& .MuiFormControlLabel-root": {
                            margin: "4px 0 !important",
                            "&:hover": {
                                backgroundColor: `${theme.palette.action.hover} !important`,
                            },
                        },
                    },
                    ".MuiDataGrid-filterPanel": {
                        backgroundColor: `${theme.palette.background.paper} !important`,
                    },
                    ".MuiDataGrid-filterForm": {
                        alignItems: "flex-end",
                    },
                })}
            />
            {showTitle && (
                <Stack
                    width="100%"
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Typography
                        variant="h6"
                        color="text.primary"
                        fontWeight="bold"
                        component="div"
                    >
                        {itemTypeText.endsWith("s")
                            ? itemTypeText
                            : `${itemTypeText}s`}
                    </Typography>
                </Stack>
            )}
            <DataGrid
                rows={rows}
                columns={orderedColumns}
                loading={isLoading}
                getRowId={getRowId}
                disableRowSelectionOnClick={disableRowSelectionOnClick}
                onRowClick={handleRowClick}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={
                    handleColumnVisibilityModelChange
                }
                disableVirtualization={false}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                processRowUpdate={processRowUpdate as any}
                getRowClassName={getRowClassName}
                slots={
                    hideToolbar
                        ? undefined
                        : {
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              toolbar: CustomToolbar as any,
                          }
                }
                slotProps={
                    hideToolbar
                        ? undefined
                        : {
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              toolbar: toolbarSlotProps as any,
                          }
                }
                sx={{
                    backgroundColor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    "& .MuiDataGrid-columnHeaders": {
                        fontSize: "0.875rem",
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? theme.palette.primary.light
                                : alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        borderTop: "1px solid",
                        borderTopColor: "divider",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                    },
                    "& .MuiDataGrid-columnHeader": {
                        borderRight: "none",
                        backgroundColor:
                            theme.palette.mode === "light"
                                ? theme.palette.primary.light
                                : alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                        color: theme.palette.primary.main,
                    },
                    "& .MuiDataGrid-sortIcon": {
                        color: `${theme.palette.primary.main} !important`,
                    },
                    "& .MuiDataGrid-menuIconButton": {
                        color: theme.palette.primary.main,
                    },
                    "& .MuiDataGrid-columnSeparator": {
                        color: theme.palette.primary.main,
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                        borderRight: "none",
                        fontSize: "0.875rem",
                        py: 1,
                        alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                        cursor: "pointer",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                    },
                    "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "action.hover",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "action.selected",
                        "&:hover": {
                            backgroundColor: "action.hover",
                        },
                    },
                    "& .MuiDataGrid-row.row-changed": {
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? `rgba(255, 152, 0, 0.18)`
                                : `rgba(255, 152, 0, 0.08)`,
                        "&:hover": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? `rgba(255, 152, 0, 0.28)`
                                    : `rgba(255, 152, 0, 0.15)`,
                        },
                    },
                    "& .MuiDataGrid-row.row-selected": {
                        backgroundColor:
                            theme.palette.mode === "dark"
                                ? alpha(theme.palette.primary.main, 0.2)
                                : alpha(theme.palette.primary.main, 0.1),
                        "& .MuiDataGrid-cell": {
                            fontWeight: 500,
                        },
                        "&:hover": {
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? alpha(theme.palette.primary.main, 0.3)
                                    : alpha(theme.palette.primary.main, 0.16),
                        },
                    },
                    "& .MuiDataGrid-toolbarContainer": {
                        padding: "8px 16px",
                        backgroundColor: "background.paper",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                        justifyContent: "center",
                        "& .MuiButton-root": {
                            fontSize: "0.875rem",
                            color: "primary.main",
                            "&:hover": {
                                backgroundColor: "action.hover",
                                color: "primary.main",
                            },
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1.2rem",
                            color: "primary.main",
                        },
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "1px solid",
                        borderTopColor: "divider",
                        backgroundColor: "background.paper",
                        fontSize: "0.875rem",
                    },
                    ...sx,
                }}
                paginationMode={paginationMode}
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                initialState={{
                    columns: {
                        columnVisibilityModel,
                        orderedFields:
                            columnOrderState.length > 0
                                ? columnOrderState
                                : undefined,
                    },
                    sorting: initialSortField
                        ? {
                              sortModel: [
                                  {
                                      field: initialSortField,
                                      sort: initialSortDirection,
                                  },
                              ],
                          }
                        : undefined,
                    pagination: paginationModel
                        ? undefined
                        : {
                              paginationModel: {
                                  page: 0,
                                  pageSize: initialPageSize,
                              },
                          },
                    filter: {
                        filterModel: {
                            items: [],
                        },
                    },
                    density,
                }}
                pageSizeOptions={pageSizeOptions}
                getRowHeight={(): "auto" => "auto"}
            />
        </Box>
    )
}

export default AdminDataGrid
export type { ToolbarButton }
export { ALL_TOOLBAR_BUTTONS }
